import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { ConfigProvider, Modal, Spin } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import { AiQuestionAndAnswer } from "lib/core_components/AiQuestionAndAnswer";
import { BidChatResponse_Sources } from "lib/generated/app-service-gql/graphql";
import { useBidChat } from "lib/hooks/api/bid/bid_chat/useBidChat";
import { useBidChatSubscription } from "lib/hooks/api/bid/bid_chat/useBidChatSubscription";
import { useGetBidChat } from "lib/hooks/api/bid/scorecard/useGetBidChat";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { settingsRoutes } from "lib/routes";
import { sysPrimaryDefault } from "lib/themes/colors";
import { MidPaddingModal } from "lib/themes/MidPaddingModal";
import { EventData, EventNames, useTracking } from "lib/tracking";
import DocumentSources from "../DocumentSources";
import AskStotlesAiChats from "./AskStotlesAiChats";
import SuggestedBidQuestions from "./SuggestedBidQuestions";

import css from "./AskStotlesAiModal.module.scss";

type AiConversation = {
  conversationId: string;
  questionAndAnswer: QaContent[];
};
type QaContent = {
  question: string;
  answer: string;
  sources: BidChatResponse_Sources[];
};

type AIQuestionForm = {
  userInputQuestion: string;
  conversations: AiConversation[];
};

type Props = {
  bidId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectDocument: (
    source: BidChatResponse_Sources,
    trackingContext: EventNames,
    trackingData: EventData,
  ) => Promise<void>;
  documentDrawerOpen: boolean;
  noticeTitle: string | undefined;
};

export default function AskStotlesAiModal({
  bidId,
  isOpen,
  onClose,
  onSelectDocument,
  documentDrawerOpen,
  noticeTitle,
}: Props) {
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();

  const { logEvent } = useTracking();

  // Used to scroll to the bottom of the question list
  const containerRef = useRef<HTMLDivElement>(null);
  const { control, handleSubmit, setValue, getValues, watch } = useForm<AIQuestionForm>({
    defaultValues: {
      conversations: [],
      userInputQuestion: "",
    },
  });

  const { conversations } = watch();

  const { data: chats, isLoading: isLoadingChats } = useGetBidChat(bidId);

  useEffect(() => {
    if (chats) {
      setValue("conversations", chats);
    }
  }, [chats, setValue]);

  const currentConversation = conversations.find(
    (c) => c.conversationId === selectedConversationId,
  );

  const scrollToBottomOfAiChat = () => {
    setTimeout(() => {
      containerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  /**
   * Starts the {@see useBidChatSubscription} subscription by setting
   * currentConversationId and appending the new question to the react hook form state
   * @param conversationId conversationId within whose context this question & answer pair exist
   * @param questionAsked the question just asked by the user
   */
  const onNewQuestionAsked = (
    conversationId: string,
    questionAsked: string,
    answer: string = "",
    sources: BidChatResponse_Sources[] = [],
  ) => {
    const newQaContent: QaContent = { question: questionAsked, answer, sources };

    setSelectedConversationId(conversationId);

    const convIndex = conversations.findIndex((c) => c.conversationId === conversationId);

    if (convIndex === -1) {
      // New conversation — add entire conversation with first Q&A
      setValue("conversations", [
        ...conversations,
        {
          conversationId,
          questionAndAnswer: [newQaContent],
        },
      ]);
    } else {
      // Append to existing conversation if ID exists
      const updatedQA = [...conversations[convIndex].questionAndAnswer, newQaContent];
      setValue(`conversations.${convIndex}.questionAndAnswer`, updatedQA);
    }

    // Set the question input field to empty
    setValue("userInputQuestion", "");

    // Scroll to the bottom of the question list when question is asked
    scrollToBottomOfAiChat();
  };

  const handleSubscriptionAnswerUpdate = (
    conversationId: string,
    answer: string | null | undefined,
    sources: BidChatResponse_Sources[],
  ) => {
    // Get the freshest values
    const currentConversations = getValues("conversations");

    // Getting the inex for react hook form update later
    const convIndex = currentConversations.findIndex((c) => c.conversationId === conversationId);

    const conversation = currentConversations[convIndex];

    // If the conversation ID is somehow not found in the form...
    // Or the answer field is blank, do nothing
    if (convIndex === -1 || !answer || conversation.questionAndAnswer.length === 0) return;

    const lastQAIndex = conversation.questionAndAnswer.length - 1;

    setValue(`conversations.${convIndex}.questionAndAnswer.${lastQAIndex}.answer`, answer);

    // The sources should come through when the answers are complete
    if (sources?.length) {
      setValue(`conversations.${convIndex}.questionAndAnswer.${lastQAIndex}.sources`, sources);
    }
  };

  const {
    isLoading: isStartingBidChatSubscription,
    mutate: startBidChatSubscription,
    reset: resetStartBidChatSubscription,
  } = useBidChat({
    onSuccess: (data) => {
      const { question, conversationId } = data.bidChat;
      onNewQuestionAsked(conversationId, question);
    },
  });

  const { data: subscriptionData } = useBidChatSubscription(currentConversation?.conversationId, {
    enabled: !!currentConversation?.conversationId && !!window.currentUser,
    onData(data, errors) {
      if (errors && errors.length > 0) {
        console.error("Bid chat subscription error:", errors);
        return;
      }

      const { conversationId, answer, sources } = data.bidChatAnswered;

      handleSubscriptionAnswerUpdate(conversationId, answer, sources ?? []);
    },
  });

  const handleQuestionSubmit = (question: string) => {
    if (question.length < 10) {
      return;
    }
    logEvent(EventNames.stotlesAiBidQuestionAsked, {
      "Action type": "Question asked",
      "Question asked": question,
    });

    startBidChatSubscription({
      req: { question, bidId, conversationId: currentConversation?.conversationId },
    });
  };

  const closeModal = () => {
    onClose();
    resetStartBidChatSubscription();
  };

  const isLoading = isStartingBidChatSubscription || subscriptionData?.isComplete === false;

  return (
    <ConfigProvider theme={MidPaddingModal}>
      <Modal
        open={isOpen}
        bodyProps={{
          style: { padding: 0 },
        }}
        // When document drawer is open, make modal appear behind it
        zIndex={documentDrawerOpen ? 2 : undefined}
        footer={null}
        closable
        maskClosable
        className={css.modal}
        onCancel={closeModal}
        onClose={closeModal}
      >
        <form
          onSubmit={handleSubmit(({ userInputQuestion }) =>
            handleQuestionSubmit(userInputQuestion),
          )}
        >
          <div className={css.askAiPanel}>
            <div className={css.historicalQuestionSection}>
              <AskStotlesAiChats
                chats={chats}
                isLoading={isLoadingChats}
                bidId={bidId}
                selectedConversationId={selectedConversationId}
                setSelectedConversationId={setSelectedConversationId}
              />
            </div>
            <div className={css.mainContent}>
              <Flex flexDirection="row" justifyContent="space-between" gap={16}>
                <Flex gap={8} flexDirection="column">
                  <Text h2>Ask Stotles AI</Text>
                  <Text>
                    Ask questions about the underlying tender documents you uploaded to this bid /
                    no-bid report. Stotles AI will sort through the sources and provide clarity.
                  </Text>
                </Flex>
              </Flex>
              <div className={css.conversations} ref={containerRef}>
                {/* Render current Q&A pairs for conversation*/}
                {currentConversation?.questionAndAnswer.map(
                  ({ question, answer, sources }, index) => (
                    <span key={index}>
                      <AiQuestionAndAnswer
                        question={question}
                        answer={answer.replace(/\\n/g, "\n")}
                        saveQuestionComponent={
                          <SaveQuestionContainer>
                            <Flex gap={8} alignItems="center">
                              <UIcon icon="infoCircle" size={20} color={sysPrimaryDefault} />
                              <Text>Save this question for future reports?</Text>
                            </Flex>
                            <Button
                              as="a"
                              buttonType="tertiary"
                              target="_blank"
                              href={settingsRoutes.bidSelfServe}
                            >
                              Add bid / no-bid criteria
                            </Button>
                          </SaveQuestionContainer>
                        }
                      />
                      {sources && sources.length > 0 && (
                        <span className={css.sources}>
                          <DocumentSources
                            sources={sources}
                            onSelectDocument={(source) =>
                              onSelectDocument(source, EventNames.citationOpened, {
                                "Action type": "Link clicked",
                                "Criteria name": "Ad-hoc question",
                              })
                            }
                            noticeTitle={noticeTitle}
                          />
                        </span>
                      )}
                    </span>
                  ),
                )}

                {/* Show suggested questions if there are no Q&A pairs */}
                {!currentConversation && !isLoading && (
                  <SuggestedBidQuestions
                    onPromptClick={(question) => {
                      setValue("userInputQuestion", question);
                      handleQuestionSubmit(question);
                    }}
                  />
                )}
              </div>
              {/* Used to scroll to the bottom of the question list */}
              <span style={{ visibility: "hidden" }} />
              <div className={css.inputContainer}>
                <TextArea
                  autoSize
                  autoFocus
                  name="userInputQuestion"
                  label={""}
                  control={control}
                  className={css.questionInput}
                  placeholder="Ask a question about the documents..."
                  rules={{
                    required: "Please enter a question",
                    minLength: { value: 10, message: "Please enter a question" },
                  }}
                  disabled={isLoading}
                  aria-label="Ask a question"
                  onPressEnter={(e) => {
                    if (e.shiftKey) {
                      // Allow newline when Shift + Enter is pressed
                      return;
                    }
                    // Prevent newline and trigger submission on Enter without Shift
                    e.preventDefault();
                    e.stopPropagation();
                    handleQuestionSubmit(e.currentTarget.value);
                  }}
                />
                {isLoading ? (
                  <Spin />
                ) : (
                  <Button
                    buttonType="primary"
                    type="submit"
                    aria-label="Ask question"
                    icon="arrowUp"
                    iconProps={{ size: 16 }}
                    // Make the button circular
                    style={{ borderRadius: 800 }}
                  />
                )}
              </div>
              <p className={css.disclaimer}>
                AI-generated responses may be inaccurate. Verify important information.
              </p>
            </div>
          </div>
        </form>
      </Modal>
    </ConfigProvider>
  );
}

const SaveQuestionContainer = styled(Flex)(({ theme }) => ({
  border: `1px solid ${theme.colors.sysBorderPrimary}`,
  borderRadius: 4,
  justifyContent: "space-between",
  padding: "10px 16px",
  alignItems: "center",
}));
